import { HttpClient } from "@/shared/http";

import { AiCreditUsage } from "./AiCreditUsage";
import { Limit, LimitDictionary } from "./LimitRecord";

export class LimitService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  async getLimitSummaryForOrg(
    orgId: string,
    options?: Partial<{
      abortSignal: AbortSignal;
    }>,
  ): Promise<LimitDictionary> {
    const url = this.#httpClient.constructUrl(`v1/limits/orgs/id/${orgId}`);
    const response = await this.#httpClient.get(url, {
      signal: options?.abortSignal,
    });

    const limits = await response.json<Limit[]>();

    return limits.reduce((dict, limit) => {
      return {
        ...dict,
        [limit.resource_name]: limit.quantity,
      };
    }, {} as LimitDictionary);
  }

  /**
   * Returns the AI credit usage snapshot rendered by the AI Credits card on
   * the Settings → Usage page (used vs. limit and the current monthly period
   * boundaries; ``limit_millionths`` is ``-1`` when no cap is configured).
   *
   * Auth is identical to ``getLimitSummaryForOrg`` above: the caller must be
   * a member of ``orgId`` or the request is rejected with 401.
   *
   * The backend serves this from the same ``AiCreditLimiter.get_usage`` call
   * that the agent worker consults before each turn, so what the user sees
   * here and what the worker enforces cannot disagree on the bucket window
   * or the used total.
   */
  async getAiCreditUsageForOrg(
    orgId: string,
    options?: Partial<{
      abortSignal: AbortSignal;
    }>,
  ): Promise<AiCreditUsage> {
    const url = this.#httpClient.constructUrl(
      `v1/limits/ai-credits/orgs/id/${orgId}`,
    );
    const response = await this.#httpClient.get(url, {
      signal: options?.abortSignal,
    });
    return response.json<AiCreditUsage>();
  }
}
